<script>
  import { apiFetch } from '../lib/auth.js';
  import { onMount, tick } from 'svelte';
  import Markdown from '../components/Markdown.svelte';
  import DatePicker from '../components/DatePicker.svelte';
  import { countryName } from '../lib/utilities.js';

  export let loggedIn = false;

  let performances = [];
  let loading      = false;
  let error        = '';
  let totalCount   = 0;

  const PAGE_SIZE = 50;
  let offset = 0;

  let reviewFilter = 'reviewed';
  let genreFilter  = '';
  let artistSearch = '';
  let dateFrom     = '';
  let dateTo       = '';
  let genres       = [];

  let expandedId     = null;
  let expandedEl     = null;
  let expandDetail   = null;
  let expandReviews  = [];
  let detailLoading  = false;
  let reviewsLoading = false;

  let searchDebounce;
  let fetchId = 0;

  let showDateFilter = false;
  let dateMode = 'range'; // 'single' | 'range'

  function pad(n) { return String(n).padStart(2, '0'); }
  function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  function shiftDate(iso, days) {
    const d = new Date((iso || todayIso()) + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  function stepFrom(days) { dateFrom = shiftDate(dateFrom, days); onFilterChange(); }
  function stepTo(days)   { dateTo   = shiftDate(dateTo,   days); onFilterChange(); }
  function stepDay(days)  { dateFrom = shiftDate(dateFrom, days); onFilterChange(); }

  onMount(() => { loadGenres(); loadPerformances(); });

  async function loadGenres() {
    try { genres = await apiFetch('/artists/genres') || []; } catch { genres = []; }
  }

  function buildParams() {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (reviewFilter === 'reviewed')   p.set('has_review', 'true');
    if (reviewFilter === 'unreviewed') p.set('has_review', 'false');
    if (genreFilter)  p.set('genre',       genreFilter);
    if (artistSearch) p.set('artist_name', artistSearch);
    if (dateFrom)     p.set('from',        dateFrom);
    if (dateTo && dateMode === 'range') p.set('to', dateTo);
    if (dateFrom && dateMode === 'single') p.set('to', dateFrom);
    return p;
  }

  async function loadPerformances() {
    loading = true; error = '';
    const id = ++fetchId;
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), 15000)
    );
    try {
      const data = await Promise.race([
        apiFetch(`/performances/past?${buildParams()}`),
        timeout,
      ]);
      if (id !== fetchId) return;
      performances = Array.isArray(data) ? data : (data.rows ?? []);
      totalCount   = data.total ?? performances.length;
    } catch (e) {
      if (id !== fetchId) return;
      error = e.message || 'Failed to load past shows';
    } finally {
      if (id === fetchId) loading = false;
    }
  }

  function setReviewFilter(f) { reviewFilter = f; offset = 0; expandedId = null; loadPerformances(); }
  function onSearchInput() {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => { offset = 0; expandedId = null; loadPerformances(); }, 300);
  }
  function onFilterChange() { offset = 0; expandedId = null; loadPerformances(); }

  $: totalPages  = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  $: currentPage = Math.floor(offset / PAGE_SIZE);

  function goPage(delta) {
    const next = currentPage + delta;
    if (next < 0 || next >= totalPages) return;
    offset = next * PAGE_SIZE;
    expandedId = null;
    loadPerformances();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function toggleExpand(p) {
    if (expandedId === p.id) { expandedId = null; expandDetail = null; expandReviews = []; return; }
    expandedId = p.id; expandDetail = null; expandReviews = [];
    detailLoading = true; reviewsLoading = true;

    await tick();
    expandedEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    try      { expandDetail = await apiFetch(`/artists/${p.artist_id}`); }
    catch    { expandDetail = null; }
    finally  { detailLoading = false; }

    try {
      const data = await apiFetch(`/reviews/public?performance_id=${p.id}`);
      expandReviews = Array.isArray(data) ? data : (data.reviews ?? []);
    } catch   { expandReviews = []; }
    finally   { reviewsLoading = false; }
  }

  function formatDate(d) {
    if (!d) return '—';
    const [y, m, day] = String(d).slice(0, 10).split('-');
    return `${day}.${m}.${y}`;
  }
  function formatTime(t) { return t ? t.slice(0, 5) : null; }

  function ratingColour(n) {
    if (n >= 8) return '#6db800';
    if (n >= 5) return '#8a7000';
    return '#663300';
  }
  function statusColour(s) {
    if (s === 'active')    return '#6db800';
    if (s === 'disbanded') return '#cc4400';
    if (s === 'hiatus')    return '#8a7000';
    return '#3a6b00';
  }

  const URL_LABELS = {
    website: 'Website', facebook: 'Facebook', instagram: 'Instagram',
    metal_archives: 'Metal Archives', wikipedia: 'Wikipedia',
    bandcamp: 'Bandcamp', spotify: 'Spotify', youtube: 'YouTube',
  };
  const urlLabel = u => URL_LABELS[u.url_type] ?? u.label ?? u.url_type ?? 'Link';
</script>

<div class="past-page">

  <div class="filter-bar">
    <div class="pill-row">
      <button class="pill" class:active={reviewFilter === 'all'}        on:click={() => setReviewFilter('all')}>All Shows</button>
      <button class="pill" class:active={reviewFilter === 'reviewed'}   on:click={() => setReviewFilter('reviewed')}>Reviewed</button>
      <button class="pill" class:active={reviewFilter === 'unreviewed'} on:click={() => setReviewFilter('unreviewed')}>Unreviewed</button>
      <button class="pill" class:active={showDateFilter && dateMode === 'single'}
        on:click={() => { const opening = !(showDateFilter && dateMode === 'single'); showDateFilter = opening; dateMode = 'single'; dateTo = ''; if (opening && !dateFrom) dateFrom = todayIso(); else if (!opening) dateFrom = ''; onFilterChange(); }}>Day</button>
      <button class="pill" class:active={showDateFilter && dateMode === 'range'}
        on:click={() => { const opening = !(showDateFilter && dateMode === 'range'); showDateFilter = opening; dateMode = 'range'; if (opening && !dateFrom) dateFrom = todayIso(); else if (!opening) { dateFrom = ''; dateTo = ''; } onFilterChange(); }}>Date Range</button>
    </div>

    <div class="search-row">
      <div class="search-box">
        <svg class="search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input class="search-input" style="padding-left:2rem" type="text" placeholder="Search artist…"
          bind:value={artistSearch} on:input={onSearchInput} />
      </div>
      {#if genres.length > 0}
        <div class="select-wrap">
          <select class="filter-select" class:active={!!genreFilter}
            bind:value={genreFilter} on:change={onFilterChange}>
            <option value="">All Genres</option>
            {#each genres as g}<option value={g}>{g}</option>{/each}
          </select>
          <svg class="select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      {/if}
    </div>

    {#if showDateFilter}
      {#if dateMode === 'single'}
        <div class="date-row">
          <span class="date-lbl">Day</span>
          <button class="step-btn" on:click={() => stepDay(-1)} title="Previous day">‹</button>
          <DatePicker bind:value={dateFrom} clearable on:change={onFilterChange} />
          <button class="step-btn" on:click={() => stepDay(1)} title="Next day">›</button>
        </div>
      {:else}
        <div class="date-row">
          <div class="date-field">
            <span class="date-lbl">From</span>
            <button class="step-btn" on:click={() => stepFrom(-1)} title="Previous day">‹</button>
            <DatePicker bind:value={dateFrom} clearable on:change={onFilterChange} />
            <button class="step-btn" on:click={() => stepFrom(1)} title="Next day">›</button>
          </div>
          <span class="date-sep">→</span>
          <div class="date-field">
            <span class="date-lbl">To</span>
            <button class="step-btn" on:click={() => stepTo(-1)} title="Previous day">‹</button>
            <DatePicker bind:value={dateTo} clearable on:change={onFilterChange} />
            <button class="step-btn" on:click={() => stepTo(1)} title="Next day">›</button>
          </div>
        </div>
      {/if}
    {/if}
  </div>

  {#if loading}
    <div class="state-msg"><span class="spinner"></span> Loading…</div>
  {:else if error}
    <div class="state-error">{error}</div>
  {:else if performances.length === 0}
    <div class="state-msg empty">No shows found.</div>
  {:else}

    <div class="result-meta">
      <span class="result-count">{totalCount.toLocaleString()}</span>
      <span class="result-label">shows</span>
    </div>

    <div class="card-list">
      {#each performances as p (p.id)}
        <div class="perf-card" class:is-open={expandedId === p.id}>

          <div class="card-header" role="button" tabindex="0"
            on:click={() => toggleExpand(p)}
            on:keydown={e => e.key === 'Enter' && toggleExpand(p)}>

            <svg class="card-chev" class:open={expandedId === p.id}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>

            <div class="card-info">
              <div class="card-r1">
                <span class="card-artist">{p.artist_name}</span>
                {#if p.artist_country}<span class="card-country">({countryName(p.artist_country) ?? p.artist_country})</span>{/if}
                {#if p.event_type === 'festival'}
                  <span class="pip festival">{p.event_name}</span>
                {/if}
              </div>
              {#if p.artist_genre?.length > 0}
                <div class="card-r2">
                  {#each p.artist_genre as g}<span class="genre-tag">{g}</span>{/each}
                </div>
              {/if}
              <div class="card-r3">
                {#if p.start_time}<span class="c-time">{formatTime(p.start_time)}</span><span class="c-dot">·</span>{/if}
                {#if p.performance_date}<span class="c-date">{formatDate(p.performance_date)}</span><span class="c-dot">·</span>{/if}
                <span class="c-venue">{p.venue_name || '—'}{#if p.venue_city}, {p.venue_city}{/if}</span>
              </div>
            </div>

            <div class="review-badge">
              {#if p.review_count > 0}
                <span class="badge-rating" style="color:{ratingColour(Math.round(p.avg_rating))};border-color:{ratingColour(Math.round(p.avg_rating))}55">
                  {Number(p.avg_rating).toFixed(1)}<span class="badge-denom">/10</span>
                </span>
                {#if p.review_count > 1}<span class="badge-count">{p.review_count} reviews</span>{/if}
              {:else}
                <span class="badge-none">—</span>
              {/if}
            </div>
          </div>

          {#if expandedId === p.id}
            <div class="card-expand" bind:this={expandedEl}>

              {#if p.sample_headline}
                <p class="expand-headline">"{p.sample_headline}"</p>
              {/if}

              {#if detailLoading}
                <div class="state-msg"><span class="spinner"></span> Loading artist…</div>
              {:else if expandDetail}
                <div class="detail-hero">
                  {#if expandDetail.band_logo_url}
                    <div class="d-logo"><img src={expandDetail.band_logo_url} alt={expandDetail.name} /></div>
                  {:else}
                    <div class="d-avatar"><span>{expandDetail.name.slice(0,2).toUpperCase()}</span></div>
                  {/if}
                  <div class="d-info">
                    <h3 class="d-name">{expandDetail.name}</h3>
                    <div class="d-tags">
                      {#if expandDetail.status}
                        <span class="d-tag" style="color:{statusColour(expandDetail.status)};border-color:{statusColour(expandDetail.status)}44">{expandDetail.status}</span>
                      {/if}
                      {#if expandDetail.formed_year}<span class="d-tag d-tag-dim">Est. {expandDetail.formed_year}</span>{/if}
                      {#if expandDetail.country}<span class="d-tag d-tag-dim">{countryName(expandDetail.country) ?? expandDetail.country}</span>{/if}
                    </div>
                    {#if expandDetail.genres?.length}
                      <div class="d-genres">{#each expandDetail.genres as g}<span class="genre-tag">{g}</span>{/each}</div>
                    {/if}
                  </div>
                </div>
                {#if expandDetail.description}
                  <div class="d-desc"><Markdown content={expandDetail.description} /></div>
                {/if}
                {#if expandDetail.urls?.length}
                  <div class="d-urls">
                    {#each expandDetail.urls as u}
                      <a class="url-chip url-{u.url_type}" href={u.url} target="_blank" rel="noopener">{urlLabel(u)}</a>
                    {/each}
                  </div>
                {/if}
              {/if}

              <div class="reviews-section">
                <span class="reviews-heading">
                  Reviews
                  {#if expandReviews.length}<span class="reviews-count">{expandReviews.length}</span>{/if}
                </span>
                {#if reviewsLoading}
                  <div class="state-msg"><span class="spinner"></span></div>
                {:else if expandReviews.length === 0}
                  <div class="state-msg empty">No public reviews for this show.</div>
                {:else}
                  {#each expandReviews as rv}
                    <div class="rv-card">
                      <div class="rv-top">
                        <div class="rv-meta">
                          <span class="rv-date">{formatDate(rv.performance_date)}</span>
                          {#if rv.venue_name}<span class="rv-sep">·</span><span class="rv-venue">{rv.venue_name}</span>{/if}
                          {#if rv.username}<span class="rv-sep">·</span><span class="rv-user">@{rv.username}</span>{/if}
                        </div>
                        <span class="rv-rating" style="color:{ratingColour(rv.rating)};border-color:{ratingColour(rv.rating)}44">{rv.rating}/10</span>
                      </div>
                      {#if rv.headline}<p class="rv-hl">"{rv.headline}"</p>{/if}
                      {#if rv.body}<div class="rv-body"><Markdown content={rv.body} /></div>{/if}
                    </div>
                  {/each}
                {/if}
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>

    {#if totalPages > 1}
      <div class="pagination">
        <button class="page-btn" on:click={() => goPage(-1)} disabled={currentPage === 0}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>Prev
        </button>
        <span class="page-info">
          <span class="page-current">{currentPage + 1}</span>
          <span class="page-sep">/</span>
          <span class="page-total">{totalPages}</span>
        </span>
        <button class="page-btn" on:click={() => goPage(1)} disabled={currentPage >= totalPages - 1}>
          Next<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  /* Filter bar */
  .filter-bar { display: flex; flex-direction: column; gap: 0.55rem; margin-bottom: 1rem; }
  .pill-row   { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .pill {
    background: none; border: 1px solid #1e3d00; border-radius: 2px; color: #2a5200;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.12em; text-transform: uppercase; padding: 0.3rem 0.65rem;
    cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .pill:hover  { color: #6db800; border-color: #3a6b00; }
  .pill.active { color: #6db800; border-color: #6db800; background: rgba(109,184,0,0.08); }

  .search-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .search-box { position: relative; flex: 1; min-width: 160px; display: flex; align-items: center; }
  .search-ico { position: absolute; left: 0.55rem; width: 13px; height: 13px; color: #2a5200; pointer-events: none; }
  /* .search-input, .filter-select, .select-wrap, .select-arrow — from global.css */
  .filter-select.active { color: #6db800; border-color: #6db800; background: rgba(109,184,0,0.08); }

  .date-row {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
    background: #060e00; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.6rem 0.75rem;
  }
  .date-field { display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 140px; }
  .date-lbl { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2a5200; flex-shrink: 0; }
  .date-sep { color: #1e3d00; font-size: 0.75rem; flex-shrink: 0; }
  .step-btn {
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 1rem; line-height: 1; padding: 0.3rem 0.45rem;
    cursor: pointer; flex-shrink: 0; transition: color 0.15s, border-color 0.15s;
  }
  .step-btn:hover { color: #6db800; border-color: #3a6b00; }
  .date-lbl { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2a5200; flex-shrink: 0; }
  .date-sep { color: #1e3d00; font-size: 0.75rem; flex-shrink: 0; }

  /* Result meta */
  .result-meta  { display: flex; align-items: baseline; gap: 0.3rem; margin-bottom: 0.5rem; }
  .result-count { font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; font-weight: 700; color: #6db800; }
  .result-label { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: #2a5200; }

  /* Card list */
  .card-list  { display: flex; flex-direction: column; border: 1px solid #1e3d00; border-radius: 2px; overflow: hidden; margin-bottom: 0.5rem; }
  .perf-card  { border-bottom: 1px solid #0f1a00; }
  .perf-card:last-child { border-bottom: none; }
  .perf-card.is-open    { background: #0a1400; }

  .card-header { display: flex; align-items: center; gap: 0.5rem; padding: 0.65rem 0.65rem 0.65rem 0.5rem; cursor: pointer; transition: background 0.1s; }
  .card-header:hover { background: #0d1a00; }
  .card-header:focus-visible { outline: 1px solid #3a6b00; outline-offset: -1px; }

  .card-chev      { width: 15px; height: 15px; flex-shrink: 0; stroke: #2a5200; transition: transform 0.2s; }
  .card-chev.open { transform: rotate(180deg); }

  .card-info    { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
  .card-r1      { display: flex; align-items: center; gap: 0.28rem; flex-wrap: wrap; }
  .card-artist  { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.95rem; color: #d4d4d4; }
  .card-country { font-family: 'Rajdhani', sans-serif; font-size: 0.78rem; font-weight: 600; color: #3a6b00; }
  .pip          { font-size: 0.44rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; border: 1px solid #1e3d00; border-radius: 2px; padding: 0.05rem 0.28rem; color: #2a5200; font-family: 'Rajdhani', sans-serif; }
  .pip.festival { color: #4a7c00; border-color: #2a5200; }
  .card-r2      { display: flex; align-items: center; gap: 0.2rem; flex-wrap: wrap; }
  .genre-tag    { font-size: 0.44rem; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; border: 1px solid #1a3300; border-radius: 2px; padding: 0.05rem 0.28rem; color: #3a6b00; font-family: 'Rajdhani', sans-serif; display: inline-block; }
  .card-r3      { display: flex; align-items: center; gap: 0.28rem; flex-wrap: wrap; }
  .c-time  { font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; font-weight: 700; color: #6db800; }
  .c-date  { font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 600; color: #4a7c00; }
  .c-dot   { color: #1e3d00; font-size: 0.55rem; }
  .c-venue { font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 600; color: #555; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  /* Review badge */
  .review-badge { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 0.15rem; }
  .badge-rating { display: inline-block; border: 1px solid; border-radius: 2px; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.82rem; padding: 0.15rem 0.45rem; white-space: nowrap; }
  .badge-denom  { font-size: 0.6rem; opacity: 0.7; }
  .badge-count  { font-family: 'Rajdhani', sans-serif; font-size: 0.45rem; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200; }
  .badge-none   { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #1e3d00; }

  /* Expand panel */
  .card-expand { background: #060d00; border-top: 1px solid #1a3300; padding: 0.85rem 1rem 1.1rem 2rem; display: flex; flex-direction: column; gap: 0.75rem; animation: slideDown 0.15s ease; }
  @media (max-width: 480px) { .card-expand { padding: 0.75rem; } }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

  .expand-headline { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.92rem; color: #c8c8c8; font-style: italic; border-left: 2px solid #3a6b00; padding-left: 0.65rem; margin: 0; }

  /* Artist detail */
  .detail-hero  { display: flex; align-items: flex-start; gap: 0.85rem; }
  .d-logo       { width: 64px; height: 64px; flex-shrink: 0; background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .d-logo img   { width: 100%; height: 100%; object-fit: contain; }
  .d-avatar     { width: 64px; height: 64px; flex-shrink: 0; background: #1a3300; border: 1px solid #2a5200; border-radius: 2px; display: flex; align-items: center; justify-content: center; }
  .d-avatar span { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.4rem; color: #6db800; }
  .d-info       { display: flex; flex-direction: column; gap: 0.3rem; flex: 1; }
  .d-name       { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.1rem; color: #fff; margin: 0; }
  .d-tags       { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .d-tag        { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.52rem; letter-spacing: 0.15em; text-transform: uppercase; border-radius: 2px; padding: 0.15rem 0.45rem; border: 1px solid; }
  .d-tag-dim    { color: #3a6b00; border-color: #1e3d00; }
  .d-genres     { display: flex; flex-wrap: wrap; gap: 0.25rem; }
  .d-desc       { font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; color: #777; line-height: 1.6; }
  .d-urls       { display: flex; flex-wrap: wrap; gap: 0.35rem; }
  .url-chip     { text-decoration: none; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem; letter-spacing: 0.12em; text-transform: uppercase; border: 1px solid #2a5200; border-radius: 2px; padding: 0.25rem 0.55rem; color: #3a6b00; background: #0a0a0a; transition: color 0.15s, border-color 0.15s; }
  .url-chip:hover      { color: #6db800; border-color: #6db800; }
  .url-spotify         { color: #1db954; border-color: #1a3300; }
  .url-bandcamp        { color: #1da0c3; border-color: #002a3a; }
  .url-metal_archives  { color: #884400; border-color: #3a1a00; }

  /* Reviews in expand */
  .reviews-section { display: flex; flex-direction: column; gap: 0.4rem; border-top: 1px solid #1a3300; padding-top: 0.7rem; }
  .reviews-heading { display: flex; align-items: center; gap: 0.4rem; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.5rem; letter-spacing: 0.3em; text-transform: uppercase; color: #2a5200; }
  .reviews-count   { background: #1a3300; border: 1px solid #2a5200; border-radius: 2px; font-size: 0.5rem; color: #6db800; padding: 0.05rem 0.3rem; }
  .rv-card  { background: #060d00; border: 1px solid #1a3300; border-radius: 2px; padding: 0.55rem 0.7rem; display: flex; flex-direction: column; gap: 0.3rem; }
  .rv-top   { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; }
  .rv-meta  { display: flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; }
  .rv-date  { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #4a7c00; }
  .rv-venue { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #3a6b00; }
  .rv-sep   { color: #1e3d00; font-size: 0.55rem; }
  .rv-user  { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; color: #2a5200; }
  .rv-rating { flex-shrink: 0; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.72rem; border: 1px solid; border-radius: 2px; padding: 0.1rem 0.35rem; }
  .rv-hl    { font-family: 'Rajdhani', sans-serif; font-size: 0.82rem; color: #999; font-style: italic; border-left: 2px solid #2a5200; padding-left: 0.45rem; margin: 0; }
  .rv-body  { padding-left: 0.45rem; font-family: 'Rajdhani', sans-serif; font-size: 0.8rem; color: #777; line-height: 1.6; }

  /* .pagination, .page-btn, .page-info, .page-current, .page-sep, .page-total — from global.css */
</style>
